import { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard, Building2, CreditCard, Tag,
  ClipboardList, FileText, LogOut, Terminal, Loader2, ChevronRight, Mail
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { name: 'Dashboard', href: '/platform-admin/dashboard', icon: LayoutDashboard },
  { name: 'Companies', href: '/platform-admin/companies', icon: Building2 },
  { name: 'Subscriptions', href: '/platform-admin/subscriptions', icon: CreditCard },
  { name: 'Pricing Requests', href: '/platform-admin/pricing-requests', icon: Mail },
  { name: 'Discounts', href: '/platform-admin/discounts', icon: Tag },
  { name: 'Onboarding Intel', href: '/platform-admin/onboarding', icon: FileText },
  { name: 'Audit Log', href: '/platform-admin/audit', icon: ClipboardList },
];

const PlatformLayout = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/auth/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not authenticated');
      const data = await res.json();
      setAdmin(data);
    } catch {
      navigate('/platform-admin/login', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    await fetch(`${API_URL}/api/platform/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    navigate('/platform-admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex" data-testid="platform-layout">
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900/80 border-r border-slate-800 flex flex-col fixed h-full z-50">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Terminal className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-100 leading-none">Platform</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Control Tower</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded text-xs font-medium transition-colors",
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                )}
                data-testid={`platform-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-3.5 h-3.5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="w-6 h-6 rounded bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-300">
              {admin?.name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-300 truncate">{admin?.name}</p>
              <p className="text-[10px] text-slate-500 truncate">{admin?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1 h-7 text-xs text-slate-400 hover:text-red-400 hover:bg-red-500/10 justify-start gap-2"
            onClick={handleLogout}
            data-testid="platform-logout"
          >
            <LogOut className="w-3 h-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-56">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur border-b border-slate-800/50 px-6 py-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Terminal className="w-3 h-3" />
            <span>Platform</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-300">
              {navItems.find(n => n.href === location.pathname)?.name || 'Dashboard'}
            </span>
          </div>
        </div>

        <div className="p-6 max-w-7xl">
          <Outlet context={{ admin }} />
        </div>
      </main>
    </div>
  );
};

export default PlatformLayout;
